import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, FileDown, Loader2, Lock, Play, Trash2, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { toast } from "sonner";
import { Panel, StatTile } from "@/components/common/Panel";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { SeverityBadge } from "@/components/common/Badges";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function Delta({ value }) {
    if (typeof value !== "number" || value === 0) {
        return <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500"><Minus size={10} /> no change</span>;
    }
    const up = value > 0;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-mono ${up ? "text-rose-300" : "text-emerald-300"}`}>
            {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {up ? `+${value}` : value} vs last digest
        </span>
    );
}

function CountRow({ label, counts }) {
    const entries = Object.entries(counts || {});
    if (!entries.length) return <p className="text-xs text-slate-500">No {label.toLowerCase()} in this window.</p>;
    return (
        <div className="flex flex-wrap gap-1.5">
            {entries.map(([k, v]) => (
                <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                    {String(k).replace(/_/g, " ")} · {v}
                </span>
            ))}
        </div>
    );
}

function ScheduleControls({ schedule, onSave, saving }) {
    const [form, setForm] = useState({
        enabled: schedule?.enabled ?? true,
        weekday: schedule?.weekday ?? 0,
        hour: schedule?.hour ?? 6,
        period_days: schedule?.period_days ?? 7,
    });
    useEffect(() => {
        setForm({
            enabled: schedule?.enabled ?? true,
            weekday: schedule?.weekday ?? 0,
            hour: schedule?.hour ?? 6,
            period_days: schedule?.period_days ?? 7,
        });
    }, [schedule]);

    return (
        <div className="flex flex-wrap items-end gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                    data-testid="digest-schedule-enabled"
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    className="accent-cyan-400"
                />
                Scheduler enabled
            </label>
            <div>
                <div className="label-eyebrow mb-1">Day (UTC)</div>
                <select
                    data-testid="digest-schedule-weekday"
                    value={form.weekday}
                    onChange={(e) => setForm({ ...form, weekday: Number(e.target.value) })}
                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                >
                    {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
            </div>
            <div>
                <div className="label-eyebrow mb-1">Hour (UTC)</div>
                <select
                    data-testid="digest-schedule-hour"
                    value={form.hour}
                    onChange={(e) => setForm({ ...form, hour: Number(e.target.value) })}
                    className="bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                >
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{`${String(h).padStart(2, "0")}:00`}</option>)}
                </select>
            </div>
            <div>
                <div className="label-eyebrow mb-1">Window (days)</div>
                <input
                    data-testid="digest-schedule-period"
                    type="number"
                    min={1}
                    max={90}
                    value={form.period_days}
                    onChange={(e) => setForm({ ...form, period_days: Number(e.target.value) })}
                    className="w-20 bg-[var(--bg-base)] border border-white/10 rounded-sm px-2 py-1.5 text-xs font-mono text-slate-200 focus:border-cyan-500/40 focus:outline-none"
                />
            </div>
            <button
                data-testid="digest-schedule-save"
                onClick={() => onSave(form)}
                disabled={saving}
                className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
            >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />} Save schedule
            </button>
        </div>
    );
}

export default function PostureDigest() {
    const { user } = useAuth();
    const isAdmin = (user?.roles || []).includes("Admin");
    const [digests, setDigests] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/digests");
            setDigests(data.digests || []);
            setSchedule(data.schedule || null);
            setSelectedId((prev) => prev || data.digests?.[0]?.id || null);
        } catch (e) {
            console.warn("Digest load failed (Admin role required)", e);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [load, isAdmin]);

    const selected = useMemo(() => digests.find((d) => d.id === selectedId) || null, [digests, selectedId]);

    const generate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/admin/digests/generate");
            toast.success(`Digest generated · ${data.period_label}`);
            setSelectedId(data.id);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Digest generation failed");
        } finally { setGenerating(false); }
    };

    const saveSchedule = async (form) => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/digests-schedule", form);
            setSchedule(data);
            toast.success("Schedule saved");
        } catch (e) {
            toast.error("Could not save the schedule");
        } finally { setSaving(false); }
    };

    const removeDigest = async (id) => {
        const target = digests.find((d) => d.id === id);
        if (!confirm(`Delete the digest for ${target?.period_label || "this window"}? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/digests/${id}`);
            if (selectedId === id) setSelectedId(null);
            toast.success("Digest deleted");
            await load();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const downloadPdf = async () => {
        if (!selected) return;
        setDownloading(true);
        try {
            const res = await api.get(`/admin/digests/${selected.id}/export/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `posture_digest_${selected.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Digest exported");
        } catch (e) {
            toast.error("Export failed");
        } finally { setDownloading(false); }
    };

    if (!isAdmin) {
        return (
            <Panel eyebrow="Restricted" title="Weekly Posture Digest">
                <p className="text-sm text-slate-400 flex items-center gap-2">
                    <Lock size={14} /> You do not have the <span className="font-mono text-cyan-300">Admin</span> role.
                    Posture digests are reserved for platform owners.
                </p>
            </Panel>
        );
    }

    const totals = selected?.totals || {};
    const deltas = selected?.deltas || {};

    return (
        <div className="space-y-6" data-testid="posture-digest-page">
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-start justify-between gap-4 relative flex-wrap">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                            <CalendarClock size={22} className="text-slate-950" strokeWidth={2.5} />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl text-white tracking-wide">Weekly Posture Digest</h1>
                            <p className="text-sm text-slate-400 mt-0.5 max-w-2xl">
                                One scheduled bundle of everything that moved: new findings, decisions taken and whether
                                each carried its Change Impact Brief, the most toxic roles, ruleset coverage and RICEFW
                                clearances. Generated offline — nothing leaves this installation.
                            </p>
                        </div>
                    </div>
                    <button
                        data-testid="digest-generate-btn"
                        onClick={generate}
                        disabled={generating}
                        className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {generating ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Generate now
                    </button>
                </div>
            </div>

            <Panel
                eyebrow="Scheduler"
                title="Weekly run"
                action={
                    schedule?.next_run_at ? (
                        <span data-testid="digest-next-run" className="text-[11px] font-mono text-cyan-300">
                            next run {String(schedule.next_run_at).slice(0, 16).replace("T", " ")} UTC
                        </span>
                    ) : null
                }
            >
                <ScheduleControls schedule={schedule} onSave={saveSchedule} saving={saving} />
                {schedule?.last_run_at && (
                    <p className="text-[11px] font-mono text-slate-500 mt-3">
                        last scheduled run {String(schedule.last_run_at).slice(0, 16).replace("T", " ")} UTC
                    </p>
                )}
            </Panel>

            {loading && (
                <Panel eyebrow="Loading" title="Digest history">
                    <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Loading digests…</div>
                </Panel>
            )}

            {!loading && digests.length === 0 && (
                <Panel eyebrow="Empty" title="No digest generated yet">
                    <p className="text-sm text-slate-400">
                        The first digest is produced by the scheduler on its next run — or press <span className="text-cyan-300">Generate now</span>.
                    </p>
                </Panel>
            )}

            {!loading && digests.length > 0 && (
                <div className="grid lg:grid-cols-[280px_1fr] gap-4">
                    <Panel eyebrow="History" title={`${digests.length} digest(s)`}>
                        <div className="space-y-1.5 max-h-[520px] overflow-y-auto">
                            {digests.map((d) => (
                                <div
                                    key={d.id}
                                    data-testid={`digest-row-${d.id}`}
                                    onClick={() => setSelectedId(d.id)}
                                    className={`p-2.5 rounded-sm border cursor-pointer transition-colors ${
                                        d.id === selectedId
                                            ? "border-cyan-500/40 bg-cyan-500/10"
                                            : "border-white/5 hover:border-white/15 bg-white/[0.02]"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-white font-medium">{d.period_label}</span>
                                        <button
                                            data-testid={`digest-delete-${d.id}`}
                                            onClick={(e) => { e.stopPropagation(); removeDigest(d.id); }}
                                            className="text-slate-500 hover:text-rose-400 transition-colors"
                                            aria-label={`Delete digest ${d.period_label}`}
                                        >
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-500 mt-1">
                                        {String(d.generated_at).slice(0, 16).replace("T", " ")} · {d.generated_by === "scheduler" ? "scheduled" : "manual"}
                                    </div>
                                    <div className="text-[10px] font-mono text-slate-400 mt-1">
                                        {d.totals?.findings_new ?? 0} new findings · {d.totals?.recommendations_decided ?? 0} decisions
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>

                    {selected && (
                        <div className="space-y-4" data-testid="digest-detail">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <StatTile testId={TID.statTile("digest-new-findings")} label="New Findings" value={totals.findings_new || 0} sub={`window ${selected.period_days}d`} />
                                <StatTile testId={TID.statTile("digest-decisions")} label="Decisions" value={totals.recommendations_decided || 0} sub={`${totals.briefs_attached || 0} with impact brief`} />
                                <StatTile testId={TID.statTile("digest-sod")} label="SoD Conflicts" value={totals.sod_conflicts || 0} sub={`${totals.roles_scanned || 0} roles scanned`} tone="red" />
                                <StatTile testId={TID.statTile("digest-open-recs")} label="Awaiting Review" value={totals.recommendations_open || 0} sub="open recommendations" />
                            </div>

                            <Panel
                                eyebrow={selected.period_label}
                                title={`${String(selected.period_start).slice(0, 10)} → ${String(selected.period_end).slice(0, 10)}`}
                                action={
                                    <button
                                        data-testid="digest-export-pdf"
                                        onClick={downloadPdf}
                                        disabled={downloading}
                                        className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                                    >
                                        {downloading ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />} Export PDF
                                    </button>
                                }
                            >
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <div className="label-eyebrow">New findings by severity</div>
                                        <CountRow label="Findings" counts={selected.findings?.by_severity} />
                                        <Delta value={deltas.findings_new} />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="label-eyebrow">New findings by engine</div>
                                        <CountRow label="Findings" counts={selected.findings?.by_engine} />
                                    </div>
                                </div>
                            </Panel>

                            <Panel eyebrow="Governance" title={`Decisions · ${selected.governance?.brief_coverage_pct ?? 0}% carry an impact brief`}>
                                {(selected.governance?.decided || []).length === 0 ? (
                                    <p className="text-sm text-slate-400">No recommendation was approved or rejected in this window.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="tbl w-full text-xs">
                                            <thead><tr><th>Recommendation</th><th>Decision</th><th>Risk</th><th>Impact brief</th></tr></thead>
                                            <tbody>
                                                {selected.governance.decided.map((r) => (
                                                    <tr key={r.id} data-testid={`digest-decision-${r.id}`}>
                                                        <td className="text-slate-200">{r.title}</td>
                                                        <td className="font-mono text-[11px] text-slate-300">{r.status}</td>
                                                        <td><SeverityBadge severity={r.risk_rating} /></td>
                                                        <td className={`font-mono text-[11px] ${r.brief_attached ? "text-emerald-300" : "text-amber-300"}`}>
                                                            {r.brief_attached ? "attached" : "missing"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Panel>

                            <Panel eyebrow="Roles" title="Top toxic roles">
                                {(selected.roles?.top_toxic || []).length === 0 ? (
                                    <p className="text-sm text-slate-400">No role in the library currently carries an SoD conflict.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="tbl w-full text-xs">
                                            <thead><tr><th>Role</th><th>SoD conflicts</th><th>Critical access</th><th>Toxicity</th></tr></thead>
                                            <tbody>
                                                {selected.roles.top_toxic.map((r) => (
                                                    <tr key={r.role_id} data-testid={`digest-toxic-${r.role_id}`}>
                                                        <td className="font-mono text-[11px] text-cyan-300">{r.role_name}</td>
                                                        <td className="text-slate-300">{r.conflict_count}</td>
                                                        <td className="text-slate-300">{r.critical_count}</td>
                                                        <td className="text-rose-300 font-mono">{r.toxicity_score}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </Panel>

                            <div className="grid md:grid-cols-2 gap-4">
                                <Panel eyebrow="Ruleset" title={`v${selected.ruleset?.version || "—"}`}>
                                    <div className="flex flex-wrap gap-1.5 text-[10px] font-mono">
                                        <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">{selected.ruleset?.risks || 0} risks</span>
                                        <span className="px-1.5 py-0.5 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/20">{selected.ruleset?.critical_activities || 0} critical activities</span>
                                        <span className="px-1.5 py-0.5 rounded-sm bg-violet-500/10 text-violet-300 border border-violet-500/25">{selected.ruleset?.enriched_functions || 0} enriched functions</span>
                                        <span className={`px-1.5 py-0.5 rounded-sm border ${
                                            (selected.ruleset?.dormant_risks || 0) > 0
                                                ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                                                : "bg-emerald-500/10 text-emerald-300 border-emerald-500/25"
                                        }`}>
                                            {selected.ruleset?.dormant_risks || 0} dormant risks
                                        </span>
                                    </div>
                                </Panel>
                                <Panel eyebrow="Code Review" title={`${selected.code_review?.sessions || 0} clearance session(s)`}>
                                    <CountRow label="Verdicts" counts={selected.code_review?.by_verdict} />
                                </Panel>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
